import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ServiceBillingBadge } from "@/components/billing/ServiceBillingBadge";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { billingSnapshotForConsumableCharge } from "@/core/billing/service-billing-status";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { resolveProductPrice } from "@/core/inventory/pharmacy-stock-sync";
import { listActiveStays } from "@/core/hospitalization/hospitalization-store";
import { submitPatientConsumableChargeToBilling } from "@/core/patient-consumables/consumable-charge-fulfillment";
import {
  createPatientConsumableCharge,
  listPatientConsumableCharges,
  PATIENT_CONSUMABLE_STATUS_LABELS,
} from "@/core/patient-consumables/patient-consumable-store";
import { getPatientById, listPatients, patientDetailPath } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { formatMoney } from "@/core/pharmacy/format";
import type { StockProduct } from "@/core/pharmacy/types";
import { routes } from "@/router/all_routes";

interface DraftLine {
  productId: string;
  productLabel: string;
  quantity: number;
}

export function PatientConsumablesPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [patientId, setPatientId] = useState("");
  const [stayId, setStayId] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [billingChargeId, setBillingChargeId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:patient-consumables", refresh);
    window.addEventListener("hospitol:billing-documents", refresh);
    return () => {
      window.removeEventListener("hospitol:patient-consumables", refresh);
      window.removeEventListener("hospitol:billing-documents", refresh);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadingProducts(true);
      try {
        const list = await pharmacyInventoryApi.listProductsForDepot("consumables");
        if (!cancelled) {
          setProducts(list.filter((p) => p.status === "ACTIVE"));
          setLoadError(null);
        }
      } catch {
        if (!cancelled) setLoadError("Catalogue WH-CONS indisponible — vérifiez l'API inventory.");
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const patients = useMemo(() => listPatients(), [refreshKey]);
  const activeStays = useMemo(() => listActiveStays(), [refreshKey]);
  const charges = useMemo(() => {
    void refreshKey;
    return listPatientConsumableCharges();
  }, [refreshKey]);

  const patientStays = useMemo(
    () => activeStays.filter((s) => s.patientId === patientId),
    [activeStays, patientId],
  );

  const selectedProduct = products.find((p) => p.id === selectedProductId) ?? null;
  const draftSubtotal = draftLines.reduce((sum, line) => {
    const product = products.find((p) => p.id === line.productId);
    const price = product ? (resolveProductPrice(product) ?? 0) : 0;
    return sum + price * line.quantity;
  }, 0);

  const pagination = useClientPagination(charges, [], 20);
  const { pagedItems } = pagination;

  const addDraftLine = () => {
    if (!selectedProduct) return;
    setDraftLines((prev) => {
      const existing = prev.find((l) => l.productId === selectedProduct.id);
      if (existing) {
        return prev.map((l) =>
          l.productId === selectedProduct.id
            ? { ...l, quantity: l.quantity + Math.max(1, quantity) }
            : l,
        );
      }
      return [
        ...prev,
        {
          productId: selectedProduct.id,
          productLabel: selectedProduct.label,
          quantity: Math.max(1, quantity),
        },
      ];
    });
    setSelectedProductId("");
    setQuantity(1);
  };

  const removeDraftLine = (productId: string) => {
    setDraftLines((prev) => prev.filter((l) => l.productId !== productId));
  };

  const handleCreateAndBill = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    if (!patientId) {
      setFormError("Sélectionnez un patient.");
      return;
    }
    if (draftLines.length === 0) {
      setFormError("Ajoutez au moins un consommable.");
      return;
    }

    setSubmitting(true);
    try {
      const charge = createPatientConsumableCharge({
        patientId,
        stayId: stayId || null,
        admissionId: patientStays.find((s) => s.id === stayId)?.admissionId ?? null,
        notes,
        lines: draftLines,
      });
      const result = await submitPatientConsumableChargeToBilling(charge.id);
      if (!result.ok) {
        setFormError(result.error ?? "Facturation impossible.");
        return;
      }
      setFormSuccess(
        result.shortage?.length
          ? `Facture créée — ${result.shortage.length} ligne(s) en rupture exclue(s).`
          : "Consommables facturés — dossier envoyé en comptabilité / caisse.",
      );
      setPatientId("");
      setStayId("");
      setNotes("");
      setDraftLines([]);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Erreur inattendue.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBillExisting = async (chargeId: string) => {
    setBillingChargeId(chargeId);
    setFormError(null);
    try {
      const result = await submitPatientConsumableChargeToBilling(chargeId);
      if (!result.ok) setFormError(result.error ?? "Facturation impossible.");
      else setFormSuccess("Fiche facturée.");
      setRefreshKey((k) => k + 1);
    } finally {
      setBillingChargeId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Consommables patient"
        description="Saisie et facturation des consommables (gants, perfusions, pansements…) depuis le stock WH-CONS."
        actions={
          <div className="ph-page-header-actions">
            <Link to={routes.hospitalization} className="btn btn-outline-secondary btn-sm">
              Hospitalisation
            </Link>
            <Link to={routes.supplyConsumablesArticles} className="btn btn-outline-secondary btn-sm">
              Stock WH-CONS
            </Link>
          </div>
        }
      />

      <div className="ph-invoice-sheet mb-3">
        <h2 className="h6 mb-3">Nouvelle fiche consommables</h2>
        {loadError ? <p className="text-danger small">{loadError}</p> : null}
        {formError ? <p className="text-danger small">{formError}</p> : null}
        {formSuccess ? <p className="text-success small">{formSuccess}</p> : null}

        <form onSubmit={handleCreateAndBill}>
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-4">
              <label className="form-label small">Patient</label>
              <select
                className="form-select form-select-sm"
                value={patientId}
                onChange={(e) => {
                  setPatientId(e.target.value);
                  setStayId("");
                }}
                required
              >
                <option value="">— Choisir —</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {patientFullName(p)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label small">Séjour actif (optionnel)</label>
              <select
                className="form-select form-select-sm"
                value={stayId}
                onChange={(e) => setStayId(e.target.value)}
                disabled={!patientId || patientStays.length === 0}
              >
                <option value="">— Aucun —</option>
                {patientStays.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.wardName} — {s.motif.slice(0, 40)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label small">Notes</label>
              <input
                className="form-control form-control-sm"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex. Trousse perfusion chambre 12"
              />
            </div>
          </div>

          <div className="row g-2 align-items-end mb-3">
            <div className="col-12 col-md-6">
              <label className="form-label small">Article WH-CONS</label>
              <select
                className="form-select form-select-sm"
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                disabled={loadingProducts}
              >
                <option value="">— Article —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                    {resolveProductPrice(p) != null ? ` — ${formatMoney(resolveProductPrice(p)!)}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6 col-md-2">
              <label className="form-label small">Qté</label>
              <input
                type="number"
                min={1}
                className="form-control form-control-sm"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
            <div className="col-12 col-sm-6 col-md-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm w-100"
                onClick={addDraftLine}
                disabled={!selectedProductId}
              >
                Ajouter
              </button>
            </div>
          </div>

          {draftLines.length > 0 ? (
            <div className="table-responsive ph-diag-table mb-3">
              <table className="table table-sm ph-dash-table mb-0">
                <thead>
                  <tr>
                    <th>Article</th>
                    <th>Qté</th>
                    <th>Prix unit.</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {draftLines.map((line) => {
                    const product = products.find((p) => p.id === line.productId);
                    const unit = product ? (resolveProductPrice(product) ?? 0) : 0;
                    return (
                      <tr key={line.productId} className="ph-diag-row">
                        <td data-label="Article">{line.productLabel}</td>
                        <td data-label="Qté">{line.quantity}</td>
                        <td data-label="Prix unit.">{formatMoney(unit)}</td>
                        <td className="text-end ph-diag-no-label" data-label="">
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0"
                            onClick={() => removeDraftLine(line.productId)}
                          >
                            Retirer
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={2}>Total estimé</th>
                    <th colSpan={2}>{formatMoney(draftSubtotal)}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="text-muted small mb-3">Aucune ligne — sélectionnez un article consommable.</p>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={submitting || draftLines.length === 0 || !patientId}
          >
            {submitting ? "Facturation…" : "Créer et facturer"}
          </button>
        </form>
      </div>

      <div className="ph-invoice-sheet">
        <h2 className="h6 mb-3">Fiches récentes</h2>
        {charges.length === 0 ? (
          <p className="text-muted small mb-0">Aucune fiche consommables patient.</p>
        ) : (
          <>
            <div className="table-responsive ph-diag-table">
              <table className="table table-sm ph-dash-table mb-0">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th>Patient</th>
                    <th>Lignes</th>
                    <th>Statut</th>
                    <th>Date</th>
                    <th>Facturation</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((charge) => {
                    const patient = getPatientById(charge.patientId);
                    const billing = billingSnapshotForConsumableCharge(charge.id);
                    return (
                      <tr key={charge.id} className="ph-diag-row">
                        <td data-label="N°" className="small fw-medium">
                          {charge.chargeNumber}
                        </td>
                        <td data-label="Patient">
                          {patient ? (
                            <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td data-label="Lignes" className="small">
                          {charge.lines.map((l) => `${l.productLabel} ×${l.quantity}`).join(", ")}
                        </td>
                        <td data-label="Statut">
                          <span className="badge text-bg-light border">
                            {PATIENT_CONSUMABLE_STATUS_LABELS[charge.status]}
                          </span>
                        </td>
                        <td data-label="Date" className="small text-muted">
                          {new Date(charge.createdAt).toLocaleString("fr-FR")}
                        </td>
                        <td data-label="Facturation">
                          <ServiceBillingBadge snapshot={billing} />
                        </td>
                        <td data-label="Actions" className="text-end">
                          {charge.status === "draft" ? (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              disabled={billingChargeId === charge.id}
                              onClick={() => void handleBillExisting(charge.id)}
                            >
                              Facturer
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </>
        )}
      </div>
    </div>
  );
}
